import { ref } from "vue";
import { supabase } from "../lib/supabase";
import { useToast } from "./useToast";
import {
  downloadGeneratedPdf,
  isDocumentGenerationTimeout,
  requestDocumentGeneration,
  waitForCompletedDocument,
} from "./useGeneratedDocument";
import { resolveStorageUrl } from "../utils/storageUrl";
import type { InspectieType } from "../../../../types";

/** Noun used in the user-facing messages of this composable. */
const SUBJECT = "het certificaat";

function triggerDownload(url: string, fileName: string) {
  const link = window.document.createElement("a");
  link.href = url;
  link.download = fileName;
  window.document.body.appendChild(link);
  link.click();
  window.document.body.removeChild(link);
}

/**
 * Download the certificaat of a rapport for an opdrachtgever.
 *
 * Two flavours:
 * - `downloadSciosCertificaat` downloads an uploaded SCIOS certificate PDF
 *   (inspectieStatus[].certificaat_pad in the userFiles bucket).
 * - `downloadInspectiecertificaat` handles NEN 1010 / NEN 3140, which have no
 *   external SCIOS certificate: it enqueues generation via the documents queue
 *   (upsert_document_generation_request), polls the documents row until the
 *   printer finishes, then downloads the signed PDF. Mirrors
 *   useHerstelSubmission.downloadHerstelFormulier.
 */
export function useInspectiecertificaat() {
  const generating = ref(false);
  const error = ref<string | null>(null);
  const toast = useToast();

  async function downloadInspectiecertificaat(
    rapportId: string,
    inspectieType: InspectieType,
    fileName: string,
  ): Promise<void> {
    generating.value = true;
    error.value = null;

    try {
      const documentId = await requestDocumentGeneration({
        type: `inspectiecertificaat_${inspectieType}`,
        rapportId,
        subject: SUBJECT,
      });

      const document = await waitForCompletedDocument(documentId, {
        subject: SUBJECT,
        onSlow: () => toast.info("Het certificaat wordt gegenereerd, dit kan even duren..."),
      });

      await downloadGeneratedPdf(document.pdf_path, fileName, SUBJECT);
    } catch (err) {
      console.error("Error downloading inspectiecertificaat:", err);
      error.value = err instanceof Error ? err.message : "Onbekende fout bij downloaden van het certificaat";
      if (isDocumentGenerationTimeout(err)) {
        // Not a defect: generation continues server-side, so keep it out of Sentry.
        toast.error((err as Error).message, err, { report: false });
      } else {
        toast.error("Het certificaat kon niet gedownload worden. Probeer het later opnieuw.", err);
      }
      throw err;
    } finally {
      generating.value = false;
    }
  }

  async function downloadSciosCertificaat(certificaatPad: string, fileName: string): Promise<void> {
    generating.value = true;
    error.value = null;

    try {
      // Signed URL so the browser gets a proper Content-Disposition filename.
      const { data: signed, error: signError } = await supabase.storage
        .from("userFiles")
        .createSignedUrl(certificaatPad, 3600, { download: fileName });

      if (!signError && signed?.signedUrl) {
        triggerDownload(signed.signedUrl, fileName);
        return;
      }

      // userFiles is a public bucket, so fall back to the public URL if signing fails.
      const publicUrl = resolveStorageUrl(certificaatPad);
      if (!publicUrl) throw signError || new Error("Geen downloadlink beschikbaar voor het certificaat");
      window.open(publicUrl, "_blank", "noopener");
    } catch (err) {
      console.error("Error downloading SCIOS certificaat:", err);
      error.value = err instanceof Error ? err.message : "Onbekende fout bij downloaden van het certificaat";
      toast.error("Het certificaat kon niet gedownload worden. Probeer het later opnieuw.", err);
      throw err;
    } finally {
      generating.value = false;
    }
  }

  return {
    generating,
    error,
    downloadInspectiecertificaat,
    downloadSciosCertificaat,
  };
}
